import { BonSortieDirecte } from '../releves/BonSortieDirecte';
import { SituationClientRetrait } from '../stocks/SituationClientRetrait';
import { useState } from 'react';
import { X, Printer, Wallet } from '../../lib/icons';
import { toast } from 'sonner';
import { PrintDoc } from '../print/PrintDoc';
import { attribuerNumero } from '../../lib/souches';
import { PaiementTiersDialog } from './PaiementTiersDialog';
import { lienCompte } from '../../lib/portail';
import {
  decompte,
  labelMode,
  labelQualite,
  familleDe,
  updateReception,
  tiersAvecCode,
  nomArticle,
  publierCompte,
  minimumPourBon,
  enregistrerMinimumPassage,
} from '../../lib/dataProvider';
import { mentionMinimum } from '../../lib/minimumTrituration';
import { sansPerte } from '../../lib/conflitEcriture';
import { useAuth } from '../../lib/auth';
import { estAdmin } from '../../lib/permissions';
import { fmt, QualiteBadge, ParcoursBadge } from '../shared/badges';

export const DecompteDialog = ({ reception: r, onClose, onUpdated }) => {
  const [rendement, setRendement] = useState(r.rendementReel ?? '');
  const [bonDirect, setBonDirect] = useState(false);
  const [impression, setImpression] = useState(false);
  const [lienSuivi, setLienSuivi] = useState('');
  const { user } = useAuth();
  const isClient = r.parcours === 'client';
  // Minimum de facturation : montant qui s'appliquerait au bon (et à son passage).
  const minTheorique = isClient ? minimumPourBon(r.id) : null;
  // Après validation, la décision figée vient du serveur (auteur, date, montant
  // abandonné imposés) : l'écran l'adopte sans attendre le rechargement de la liste.
  const [minFige, setMinFige] = useState(null);
  const minCourant = minFige ?? r.minTrit;
  const dejaFige = minCourant?.fige === true;
  const [applique, setApplique] = useState(
    r.minTrit?.fige === true ? r.minTrit.actif === true && !r.minTrit.derogation : minTheorique?.actif === true
  );
  const [noteDerog, setNoteDerog] = useState(r.minTrit?.derogation?.note ?? '');
  const peutDeroger = estAdmin(user?.role) || user?.peut_deroger_minimum === true;
  const minAffiche = !isClient
    ? null
    : dejaFige
      ? minCourant
      : !minTheorique
        ? null
        : applique
          ? minTheorique
          : {
              ...minTheorique,
              actif: false,
              complement: 0,
              derogation:
                minTheorique.complement > 0
                  ? { par: user?.name ?? '—', le: null, montantAbandonne: minTheorique.complement, note: noteDerog || null }
                  : null,
            };

  // Le bon porte le QR PERMANENT du tiers (jamais régénéré) : il ouvre son
  // relevé de compte à jour ; l'instantané est republié à l'impression.
  const ouvrirImpression = async () => {
    setLienSuivi(lienCompte(r.tiersId));
    setImpression(true);
    await publierCompte(r.tiersId);
  };
  const [paiement, setPaiement] = useState(false);
  const [enchainerPaiement, setEnchainerPaiement] = useState(false);
  const [calamites, setCalamites] = useState(r.calamites === true);
  const c = decompte({ ...r, rendementReel: rendement || r.rendementReel, calamites, minTrit: minAffiche });
  const famille = familleDe(r.qualite);

  // À la validation, le bon s'ouvre seul en deux exemplaires et lance l'impression ;
  // la fenêtre de règlement s'enchaîne à la fermeture de l'aperçu.
  const valider = async () => {
    if (!isClient && r.mode === 'reel' && !Number(rendement)) return toast.error('Saisissez le rendement réel');
    // Décision sur le minimum de facturation : enregistrée pour TOUT le passage,
    // puis figée (le serveur recalcule le complément et contrôle la permission).
    if (isClient && !dejaFige) {
      const { error, valeur } = await sansPerte(() =>
        enregistrerMinimumPassage(r.id, { applique, note: noteDerog, par: user?.name })
      );
      if (error) return;
      if (valeur?.error) return toast.error(valeur.error);
    }
    const { error: refus, valeur: majBon } = await sansPerte(() =>
      updateReception(r.id, {
        rendementReel: Number(rendement) || r.rendementReel,
        calamites: isClient ? false : calamites,
        statut: 'Décompté',
      })
    );
    if (refus) return;
    if (majBon?.minTrit) setMinFige(majBon.minTrit);
    onUpdated();
    setEnchainerPaiement(true);
    await ouvrirImpression();
  };

  if (paiement) {
    return (
      <PaiementTiersDialog
        tiersId={r.tiersId}
        onClose={() => {
          setPaiement(false);
          onClose();
        }}
        onDone={onUpdated}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="decompte-dialog">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">
              {isClient ? 'Facture client' : 'Décompte agriculteur'} — {r.id}
            </h2>
            <p className="text-sm text-slate-600">{tiersAvecCode(r.tiersId)}</p>
          </div>
          <button
            type="button"
            data-testid="decompte-close"
            onClick={onClose}
            className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {isClient && r.sortieDirecte === true && ['Trituré','Décompté'].includes(r.statut) && Number(r.huileExtraite) > 0 && <>
            <SituationClientRetrait tiersId={r.tiersId} />
            <button type="button" data-testid="bon-sortie-directe" onClick={() => setBonDirect(true)} className="border border-slate-400 px-3 py-2 font-bold">Imprimer le bon de sortie d’huile</button>
          </>}

          <div className="flex flex-wrap items-center gap-2">
            <ParcoursBadge parcours={r.parcours} />
            {r.qualite && <QualiteBadge famille={famille} label={labelQualite(r.qualite)} />}
            <span className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              {labelMode(r.mode)}
            </span>
          </div>

          {famille === 'lampante' && !isClient && (
            <p className="border-l-2 border-orange-500 bg-orange-50 px-3 py-2 text-xs text-orange-800">
              Comptabilité LAMPANTE cloisonnée — hors bilan des huiles nobles.
            </p>
          )}

          {!isClient && r.mode === 'reel' && (
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                Rendement réel décompteur (%)
              </label>
              <input
                data-testid="input-rendement-reel"
                type="number"
                step="0.1"
                value={rendement}
                onChange={(e) => setRendement(e.target.value)}
                className="mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
              />
            </div>
          )}

          <dl className="grid grid-cols-2 gap-px border border-slate-200 bg-slate-200">
            {[
              ['Poids brut', `${fmt(r.poidsBrut)} Kg`],
              ['Tare', `${fmt(r.tare)} Kg`],
              ['Poids net', `${fmt(c.poidsNet)} Kg`],
              ['Rendement retenu', `${fmt(c.rendement, 2)} %`],
              ['Huile', `${fmt(c.huile)} L`],
              isClient
                ? ['Trituration', `${fmt(c.trituration, 2)} TND`]
                : ['Prix unitaire', `${fmt(c.prixKg, 3)} TND/kg`],
            ].map(([k, v]) => (
              <div key={k} className="bg-white px-3 py-2">
                <dt className="text-xs text-slate-500">{k}</dt>
                <dd className="tabular font-data text-sm font-semibold text-slate-900">{v}</dd>
              </div>
            ))}
          </dl>

          {isClient && minAffiche && (Number(minAffiche.complementPassage) > 0 || Number(minAffiche.complement) > 0 || minAffiche.derogation) && (
            <div data-testid="decompte-minimum" className="border border-slate-200">
              <p className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                Minimum de facturation — seuil {fmt(minAffiche.seuil)} kg
                {minAffiche.estimation && (
                  <span data-testid="decompte-minimum-estimation" className="border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-amber-800">
                    estimation — passage non encore constitué
                  </span>
                )}
                {(minAffiche.bons?.length ?? 1) > 1 && (
                  <span data-testid="decompte-minimum-passage" className="border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-sky-800">
                    passage de {minAffiche.bons.length} bons — {fmt(minAffiche.baseKg)} kg
                  </span>
                )}
              </p>
              <dl>
                {[
                  ['Poids net réellement reçu', `${fmt(c.poidsNet)} Kg`],
                  ['Trituration', `${fmt(c.poidsNet)} Kg × ${fmt(c.tarifTrituration, 3)} TND = ${fmt(c.triturationBase, 3)} TND`],
                  [
                    `Complément minimum de facturation — seuil ${fmt(minAffiche.seuil)} kg`,
                    minAffiche.derogation ? 'non appliqué — dérogation' : `${fmt(c.minimumComplement, 3)} TND`,
                  ],
                  ['Total prestation de trituration', `${fmt(c.trituration, 3)} TND`],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0">
                    <dt className="text-xs text-slate-500">{k}</dt>
                    <dd className="tabular font-data text-sm font-semibold text-slate-900">{v}</dd>
                  </div>
                ))}
              </dl>
              <label
                data-testid="decompte-toggle-minimum"
                className={`flex cursor-pointer items-start gap-3 border-t border-slate-200 px-3 py-3 transition-colors duration-150 ${
                  applique ? 'bg-slate-50' : 'bg-white'
                } ${dejaFige || !peutDeroger ? 'cursor-not-allowed opacity-80' : 'hover:bg-slate-50'}`}
              >
                <input
                  data-testid="decompte-input-minimum"
                  type="checkbox"
                  checked={applique}
                  disabled={dejaFige || !peutDeroger}
                  onChange={(e) => setApplique(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-slate-900"
                />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">Appliquer le minimum de trituration</span>
                  <span className="mt-0.5 block font-data text-xs text-slate-500">
                    {dejaFige
                      ? 'Décision déjà enregistrée avec l’opération : toute correction passe par un avoir.'
                      : peutDeroger
                        ? 'Décochez pour accorder une dérogation commerciale. Elle s’applique à tout le passage ; auteur, date et montant abandonné sont conservés en interne.'
                        : 'Permission « Peut déroger au minimum de trituration » requise — demandez-la à l’Administrateur.'}
                  </span>
                </span>
              </label>
              {!applique && !dejaFige && Number(minTheorique?.complement) > 0 && peutDeroger && (
                <div className="border-t border-slate-200 px-3 py-3">
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                    Note de dérogation (facultative)
                  </label>
                  <input
                    data-testid="decompte-note-derogation"
                    value={noteDerog}
                    onChange={(e) => setNoteDerog(e.target.value)}
                    placeholder="Motif commercial…"
                    className="mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                  />
                  <p data-testid="decompte-minimum-abandonne" className="mt-1.5 font-data text-[11px] text-amber-800">
                    Complément abandonné : {fmt(minTheorique.complement, 3)} TND
                  </p>
                </div>
              )}
              {dejaFige && minCourant?.derogation && (
                <p data-testid="decompte-minimum-derogation" className="border-t border-slate-200 bg-amber-50 px-3 py-2 font-data text-[11px] text-amber-900">
                  Dérogation enregistrée — complément abandonné {fmt(minCourant.derogation.montantAbandonne, 3)} TND
                  {minCourant.derogation.par ? ` · ${minCourant.derogation.par}` : ''}
                  {minCourant.derogation.le ? ` · ${String(minCourant.derogation.le).slice(0, 10)}` : ''}
                </p>
              )}
            </div>
          )}

          {isClient && (r.ventes || []).length > 0 && (
            <div data-testid="facture-emballages" className="border border-slate-200">
              <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                Comptoir — emballages vendus
              </p>
              <ul className="divide-y divide-slate-100">
                {r.ventes.map((v) => (
                  <li key={v.articleId} className="flex justify-between px-3 py-1.5 text-sm">
                    <span className="text-slate-700">{nomArticle(v.articleId)}</span>
                    <span className="font-data text-slate-900">× {fmt(v.qte)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between border-t border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-500">Total emballages</span>
                <span className="tabular font-data font-semibold text-slate-900">{fmt(c.emballages, 2)} TND</span>
              </div>
            </div>
          )}

          {!isClient && (
            <label
              data-testid="decompte-toggle-calamites"
              className={`flex cursor-pointer items-start gap-3 border px-3 py-3 transition-colors duration-150 ${
                calamites ? 'border-slate-900 bg-slate-50' : 'border-slate-300 bg-white hover:bg-slate-50'
              }`}
            >
              <input
                data-testid="decompte-input-calamites"
                type="checkbox"
                checked={calamites}
                onChange={(e) => setCalamites(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-slate-900"
              />
              <span>
                <span className="block text-sm font-semibold text-slate-900">
                  Déduire le fonds de calamité au fournisseur (1 %)
                </span>
                <span className="mt-0.5 block font-data text-xs text-slate-500">
                  Case cochée : retenue sur le fournisseur. Case décochée : charge de l’huilerie.
                  Le fonds reste dû dans les deux cas.
                </span>
              </span>
            </label>
          )}

          {!isClient && (
            <dl data-testid="decompte-retenues" className="border border-slate-200">
              {[
                ['Montant brut', `${fmt(c.montantBrut, 3)} TND`],
                ['Fonds des Calamités (1 %)', calamites ? `− ${fmt(c.retenueCalamites, 3)} TND` : '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-100 px-3 py-2 last:border-b-0">
                  <dt className="text-xs uppercase tracking-[0.08em] text-slate-500">{k}</dt>
                  <dd className="tabular font-data text-sm font-semibold text-slate-900">{v}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="flex items-center justify-between border border-slate-900 bg-slate-900 px-4 py-3 text-white">
            <span className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-300">
              {isClient ? c.sens : 'Net à payer au fournisseur'}
            </span>
            <span data-testid="decompte-montant" className="tabular font-data text-xl font-semibold">
              {fmt(c.montant, 2)} TND
            </span>
          </div>

          {!isClient && (
            <p className="font-data text-[11px] text-slate-500">
              {r.mode === 'fixe'
                ? `Prix fixe négocié : ${fmt(r.prixFixe ?? 0, 3)} TND/kg`
                : r.mode === 'foss'
                  ? `Mode Rendement FOSS — prix de base de l’huile convenu : ${fmt(c.prixBaseHuile, 3)} TND/kg`
                  : `Prix de base de l’huile convenu : ${fmt(c.prixBaseHuile, 3)} TND`}
            </p>
          )}
          {isClient && (
            <p className="font-data text-[11px] text-slate-500">
              Prix de trituration saisi : {fmt(c.tarifTrituration, 3)} TND/kg
            </p>
          )}
        </div>

        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            data-testid="imprimer-bon"
            onClick={ouvrirImpression}
            className="flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <Printer size={15} /> Imprimer / PDF
          </button>
          <button
            type="button"
            data-testid="decompte-valider"
            onClick={valider}
            className="flex h-11 flex-1 items-center justify-center gap-2 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <Wallet size={15} /> Régler / Avance
          </button>
        </div>
      </div>
      {bonDirect && <BonSortieDirecte reception={r} onClose={() => setBonDirect(false)} />}
      {impression && (
        <PrintDoc
          type={isClient ? 'Ticket de prestation — trituration' : 'Bon de réception & décompte'}
          format={isClient ? 'a4' : undefined}
          compact={isClient}
          numero={attribuerNumero('bon_reception', r.id)}
          emission={
            isClient
              ? {
                  type: 'prestation_reception',
                  id: r.id,
                  libelle: 'Prestation de trituration',
                  date: r.date,
                  tiersId: r.tiersId,
                  tiers: tiersAvecCode(r.tiersId),
                  referenceInterne: r.id,
                  total: c.fraisTrituration ?? c.montant ?? 0,
                  lignes: [
                    ['Qualité', r.qualite ? labelQualite(r.qualite) : '—'],
                    ['Poids net', `${fmt(c.poidsNet)} Kg`],
                    ['Rendement retenu', `${fmt(c.rendement, 2)} %`],
                    ['Huile', `${fmt(c.huile)} L`],
                    ...(Number(c.minimumComplement) > 0
                      ? [[`Complément minimum de facturation — seuil ${fmt(minAffiche?.seuil ?? 0)} kg`, `${fmt(c.minimumComplement, 3)} TND`]]
                      : []),
                  ],
                  totaux: [['Montant de la prestation', `${fmt(c.fraisTrituration ?? c.montant ?? 0, 3)} TND`]],
                }
              : undefined
          }
          date={r.date}
          tiers={tiersAvecCode(r.tiersId)}
          lignes={[
            ['Parcours', isClient ? 'Client — prestation de trituration' : 'Fournisseur — achat d’olives'],
            ...(r.immatriculation ? [['Immatriculation', r.immatriculation]] : []),
            ['Qualité', r.qualite ? labelQualite(r.qualite) : '—'],
            ['Mode de calcul', labelMode(r.mode)],
            ...(!isClient && r.mode === 'foss'
              ? [['Prix de base de l’huile convenu', `${fmt(c.prixBaseHuile, 3)} TND/kg`]]
              : []),
            ['Poids brut', `${fmt(r.poidsBrut)} Kg`],
            ['Tare', `${fmt(r.tare)} Kg`],
            ['Poids net', `${fmt(c.poidsNet)} Kg`],
            ...(c.tauxDechet > 0 ? [['Taux de déchet fosse', `${fmt(c.tauxDechet, 1)} % — poids utile ${fmt(c.poidsUtile)} Kg`]] : []),
            ...(r.evaluation ? [['Évaluation fosse', r.evaluation]] : []),
            ['Rendement retenu', `${fmt(c.rendement, 2)} %`],
            ['Huile', `${fmt(c.huile)} L`],
            ['Statut', r.statut],
          ]}
          totaux={
            isClient
              ? [
                  ['Poids net réellement reçu', `${fmt(c.poidsNet)} Kg`],
                  ['Trituration', `${fmt(c.poidsNet)} Kg × ${fmt(c.tarifTrituration, 3)} TND = ${fmt(c.triturationBase, 3)} TND`],
                  ...(Number(c.minimumComplement) > 0
                    ? [[`Complément minimum de facturation — seuil ${fmt(minAffiche?.seuil ?? 0)} kg`, `${fmt(c.minimumComplement, 3)} TND`]]
                    : []),
                  ['Total prestation de trituration', `${fmt(c.trituration, 3)} TND`],
                  ['Emballages', `${fmt(c.emballages, 2)} TND`],
                  ['Total à facturer', `${fmt(c.montant, 2)} TND`],
                ]
              : [
                  ['Prix unitaire', `${fmt(c.prixKg, 3)} TND/kg`],
                  ['Montant brut', `${fmt(c.montantBrut, 3)} TND`],
                  ...(calamites
                    ? [['Fonds des Calamités (1 %)', `− ${fmt(c.retenueCalamites, 3)} TND`]]
                    : []),
                  ['Net à payer', `${fmt(c.montant, 3)} TND`],
                ]
          }
          mentions={
            [
              famille === 'lampante' ? 'Filière LAMPANTE — comptabilité et stockage cloisonnés.' : null,
              isClient ? mentionMinimum(minAffiche) : null,
              calamites && !isClient
                ? 'Retenue Fonds des Calamités (1 %) prélevée à la source par l’huilerie et reversée à l’État. Seul le net à payer est porté au compte du fournisseur.'
                : null,
            ]
              .filter(Boolean)
              .join(' ') || null
          }
          qrUrl={lienSuivi}
          qrLegende="Votre QR permanent : scannez pour votre relevé de compte à jour"
          exemplaires={['Exemplaire huilerie', isClient ? 'Exemplaire client' : 'Exemplaire fournisseur']}
          autoPrint
          onClose={() => {
            setImpression(false);
            if (enchainerPaiement) {
              setEnchainerPaiement(false);
              setPaiement(true);
            }
          }}
        />
      )}
    </div>
  );
};
